// Zadanie zamkniete E8: zapis sytuacji tekstowej w postaci wyrazenia algebraicznego.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];
const COLOR_SETS = [
  ["fioletowych", "białych", "czarnych"],
  ["zielonych", "żółtych", "czerwonych"],
  ["niebieskich", "białych", "pomarańczowych"],
];

function pick(values) {
  return values[Math.floor(Math.random() * values.length)];
}

function shuffle(values) {
  const result = [...values];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function expressionLatex(coefficient, constant) {
  const variablePart = coefficient === 1 ? "x" : `${coefficient}x`;
  if (constant === 0) return variablePart;
  return `${variablePart}${constant > 0 ? "+" : "-"}${Math.abs(constant)}`;
}

function createOptions(correctCoefficient, constant, distractorCoefficient) {
  const values = shuffle([
    { coefficient: correctCoefficient, constant },
    { coefficient: correctCoefficient, constant: -constant },
    { coefficient: distractorCoefficient, constant },
    { coefficient: distractorCoefficient, constant: -constant },
  ]);
  return {
    options: values.map((value, index) => ({
      label: LETTERS[index],
      ...value,
      latex: expressionLatex(value.coefficient, value.constant),
    })),
    correctIndex: values.findIndex(
      (value) =>
        value.coefficient === correctCoefficient && value.constant === constant
    ),
  };
}

function createOriginalTask() {
  return {
    type: "E8CollectionExpressionClosedTask",
    displayBaseNumber: 6,
    data: {
      colors: ["fioletowych", "białych", "czarnych"],
      multiplier: 4,
      difference: 3,
      relation: "less",
      correctCoefficient: 9,
      constant: 3,
      options: [
        { label: "A", coefficient: 9, constant: 3, latex: "9x+3" },
        { label: "B", coefficient: 9, constant: -3, latex: "9x-3" },
        { label: "C", coefficient: 6, constant: 3, latex: "6x+3" },
        { label: "D", coefficient: 6, constant: -3, latex: "6x-3" },
      ],
      correctIndex: 0,
    },
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 6,
    },
    tags: ["e8", "algebra", "wyrazenia_algebraiczne", "zadanie_tekstowe"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  const colors = pick(COLOR_SETS);
  const multiplier = pick([2, 3, 4, 5]);
  const difference = pick([2, 3, 4, 5, 6]);
  const relation = pick(["less", "more"]);
  const constant = relation === "less" ? difference : -difference;
  const correctCoefficient = 1 + 2 * multiplier;
  const distractorCoefficient = multiplier + 2;
  const { options, correctIndex } = createOptions(
    correctCoefficient,
    constant,
    distractorCoefficient
  );

  return {
    type: "E8CollectionExpressionClosedTask",
    displayBaseNumber: 6,
    data: {
      colors,
      multiplier,
      difference,
      relation,
      correctCoefficient,
      constant,
      options,
      correctIndex,
    },
    source: { variant: "random", basedOn: "E8_2025_MAJ_6" },
    tags: ["e8", "algebra", "wyrazenia_algebraiczne", "zadanie_tekstowe"],
  };
}

export function renderTask({ data }) {
  const [baseColor, multipliedColor, comparedColor] = data.colors;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        W pudełku znajdują się wyłącznie piłki {baseColor}, {multipliedColor} i{" "}
        {comparedColor}. Piłek {multipliedColor} jest{" "}
        <InlineMath math={`${data.multiplier}`} /> razy więcej niż {baseColor} i
        o <InlineMath math={`${data.difference}`} />{" "}
        {data.relation === "less" ? "mniej" : "więcej"} niż {comparedColor}.
        Liczbę piłek {baseColor} oznaczymy przez <InlineMath math="x" />.
      </p>
      <p>
        <strong>
          Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.
        </strong>
      </p>
      <p>Łączną liczbę wszystkich piłek w pudełku opisuje wyrażenie</p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: "8px",
        }}
      >
        {data.options.map((option) => (
          <div key={option.label}>
            <ChoiceOptionMath letter={option.label} math={option.latex} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({
        value: index,
        label: option.label,
        textNode: option.label,
      }))}
      value={userAnswer?.choiceIndex ?? null}
      onChange={(choice) => {
        const choiceIndex =
          typeof choice === "number"
            ? choice
            : Number(choice?.target?.value ?? choice?.value);
        onChange({ choiceIndex });
      }}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer?.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const option = data.options[data.correctIndex];
  return <ChoiceOptionMath letter={option.label} math={option.latex} />;
}

export function renderUserAnswer(userAnswer, task) {
  const option = task?.data?.options?.[userAnswer?.choiceIndex];
  return option ? (
    <ChoiceOptionMath letter={option.label} math={option.latex} />
  ) : (
    <i>(brak odpowiedzi)</i>
  );
}

export function renderExplanation({ data }) {
  const [baseColor, multipliedColor, comparedColor] = data.colors;
  const multipliedLatex = `${data.multiplier}x`;
  const comparedConstant =
    data.relation === "less" ? data.difference : -data.difference;
  const comparedLatex = expressionLatex(
    data.multiplier,
    comparedConstant
  );
  const correctOption = data.options[data.correctIndex];

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Zapisujemy liczbę piłek {baseColor}.
      </p>
      <BlockMath math={`\\text{${baseColor.replace("ych", "e")}}:\\quad x`} />
      <p>
        <strong>Krok 2.</strong> Piłek {multipliedColor} jest{" "}
        <InlineMath math={`${data.multiplier}`} /> razy tyle, więc:
      </p>
      <BlockMath
        math={`\\text{${multipliedColor.replace("ych", "e")}}:\\quad ${data.multiplier}\\cdot x=${multipliedLatex}`}
      />
      <p>
        <strong>Krok 3.</strong> Wyznaczamy liczbę piłek {comparedColor}.
        Piłek {multipliedColor} jest o{" "}
        <InlineMath math={`${data.difference}`} />{" "}
        {data.relation === "less" ? "mniej" : "więcej"}, zatem piłek{" "}
        {comparedColor} jest o tyle{" "}
        {data.relation === "less" ? "więcej" : "mniej"}.
      </p>
      <BlockMath
        math={`\\text{${comparedColor.replace("ych", "e")}}:\\quad ${comparedLatex}`}
      />
      <p>
        <strong>Krok 4.</strong> Dodajemy liczby piłek wszystkich trzech
        kolorów i redukujemy wyrazy podobne.
      </p>
      <BlockMath
        math={`x+${multipliedLatex}+\\left(${comparedLatex}\\right)=${correctOption.latex}`}
      />
      <p>
        <strong>Krok 5.</strong> Poprawna jest odpowiedź{" "}
        <ChoiceOptionMath letter={correctOption.label} math={correctOption.latex} />
        .
      </p>
    </div>
  );
}
